import { API } from "apis";
import PageTitle from "components/common/PageTitle";
import ConfirmDialog from "components/dialog/ConfirmDIalog";
import ExhibitForms from "components/ExhibitForms";
import ExhibitFormsConfirm from "components/ExhibitFormsConfirm";
import { ApplyStatusType, SaleStatusType } from "enums";
import { usePrompt } from "hooks/usePrompt";
import {
  ApplyItemDetailModel,
  CategoryModel,
  ContextModel,
  HideListItemModel,
  LimitedListItemModel,
} from "models";
import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { Utils } from "utils";

const ExhibitEntryPage: React.FC = () => {
  const { item_cd } = useParams();
  const navigate = useNavigate();

  const ctx = useOutletContext<ContextModel>();
  const didEffect = React.useRef(false);

  const [data, setData] = React.useState<ApplyItemDetailModel | null>(null);
  const category = React.useRef<CategoryModel[]>([]);
  const limitedLists = React.useRef<LimitedListItemModel[]>([]);
  const hideLists = React.useRef<HideListItemModel[]>([]);
  // この会社の他の商品
  const otherItems = React.useRef<{ item_cd: string; item_name: string }[]>([]);
  const [formIsDirty, setFormIsDirty] = React.useState(true);
  const [confirm, setConfirm] = React.useState(false);
  // 更新時に申請が必要かどうか
  const [isApprovalRequired, setIsApprovalRequired] = React.useState(false);

  const [confirmDialog, setConfirmDialog] = React.useState<{
    message: string;
    callback: (result: boolean) => void;
  } | null>(null);

  const [deepFileChanged, setDeepFileChanged] = React.useState(null)

  const onBeforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    // ここに設定する文字列は表示には影響しない？
    e.returnValue = "check";
  };

  usePrompt("入力中の内容は破棄されます。よろしいですか？", formIsDirty);

  // 確認画面でのタイトルを生成して返します。
  const getConfirmTitle = () => {
    if (data?.sale_status === null) {
      return "以下の内容で出品情報の登録を申請します。";
    } else {
      if (isApprovalRequired) {
        return "以下の内容で出品情報の更新を申請します。";
      } else {
        return "以下の内容で出品情報を更新します。";
      }
    }
  };
  // 確認画面でのボタンラベルを生成して返します。
  const getConfirmButtonLabel = () => {
    if (data?.sale_status === null) {
      return "申請する";
    } else {
      if (isApprovalRequired) {
        return "申請する";
      } else {
        return "更新する";
      }
    }
  };
  // 登録/更新/申請完了後のメッセージを生成して返します。
  const getCompleteLabel = () => {
    if (data?.sale_status === null) {
      return "出品情報の登録を申請しました。";
    } else {
      if (isApprovalRequired) {
        return "出品情報の更新を申請しました。";
      } else {
        return "出品情報の更新が完了しました。";
      }
    }
  };

  const onToConfirm = async () => {
    if (!data) {
      return;
    }

    if (data.apply_is_discretionary_price) {
      // 個別見積もり:ありの場合は、価格の入力値を削除
      data.apply_unit_price = "";
    }

    if(data.apply_unit_price) {
      data.apply_unit_price = +`${data.apply_unit_price}`.replace(",", "")
    }

    ctx.loading.setState(true);
    try {
      if (!item_cd) {
        // 作成
        const response = await API.ApplyItem.preCheckCreate(data);
        console.log(response);
        setIsApprovalRequired(false);
      } else {
        const response = await API.ApplyItem.preCheckUpdate(data);
        console.log(response);
        setIsApprovalRequired(response.data.is_approval_required);
      }
      setConfirm(true);
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
    } catch (err) {
      Utils.errorAction(ctx, err);
    }
    ctx.loading.setState(false);
  };

  const onCancel = () => {
    setConfirmDialog({
      message: "この申請を取り消します。よろしいですか？",
      callback: async (result: boolean) => {
        setConfirmDialog(null);
        if (!result) {
          return;
        }
        if (!item_cd) {
          return;
        }

        setFormIsDirty(false);
        ctx.loading.setState(true);
        try {
          const response = await API.ApplyItem.del({ apply_item_cd: item_cd });
          console.log(response);

          navigate("/exhibit-list/");
        } catch (err) {
          Utils.errorAction(ctx, err);
        }
        ctx.loading.setState(false);
      },
    });
  };

  const onSaleStatusChange = (flg: boolean) => {
    const message = flg
      ? "この商品の販売を再開します。よろしいですか？"
      : "この商品の販売を停止します。よろしいですか？";
    setConfirmDialog({
      message,
      callback: async (result: boolean) => {
        setConfirmDialog(null);
        if (!result) {
          return;
        }
        if (!item_cd) {
          return;
        }

        ctx.loading.setState(true);
        try {
          {
            const response = await API.ApplyItem.updateSaleStatus({
              item_cd,
              sale_status: flg
                ? SaleStatusType._1_HANBAI_CHU
                : SaleStatusType._2_TEISHI,
            });
            console.log(response);
          }
          // データ更新
          const response = await API.ApplyItem.detail({ item_cd });
          console.log(response);
          setData(response.data);
        } catch (err) {
          Utils.errorAction(ctx, err);
        }
        ctx.loading.setState(false);
      },
    });
  };
  const onBack = () => {
    setConfirm(false);
  };

  const onNext = async () => {
    if (!data) {
      return;
    }
    setFormIsDirty(false);
    ctx.loading.setState(true);

    setFormIsDirty(false);
    try {
      const response = item_cd
        ? await API.ApplyItem.update(data)
        : await API.ApplyItem.create(data);
      console.log(response);

      ctx.msgDialog.setState({
        message: getCompleteLabel(),
        callback: () => navigate("/exhibit-list/"),
      });
    } catch (err) {
      Utils.errorAction(ctx, err);
    }

    ctx.loading.setState(false);
  };

  React.useEffect(() => {
    if (didEffect.current) {
      return;
    }
    didEffect.current = true;
    const f = async () => {
      try {
        await ctx.auth.initialize();

        {
          const response = await API.CategoryItem.list(); // カテゴリ
          console.log(response);
          category.current = response.data;
        }
        {
          const response = await API.LimitedList.list(); // 限定公開リスト
          console.log(response);
          limitedLists.current = response.data;
        }
        {
          const response = await API.HideList.list(); // 非公開リスト
          console.log(response);
          hideLists.current = response.data;
        }
        {
          const response = await API.ApplyItem.list(); // 他の自社商品
          console.log(response);
          otherItems.current = response.data;
        }
      } catch (err) {
        Utils.errorAction(ctx, err);
      }

      if (item_cd) {
        // 編集
        try {
          const response = await API.ApplyItem.detail({ item_cd });
          console.log(response);

          setData(response.data);
          if(item_cd) {
            const { apply_item_main_image, apply_item_sub_image,  apply_item_append_pdf } = response.data;
            setDeepFileChanged({
              item_main_image: {...apply_item_main_image},
              item_sub_image: [...apply_item_sub_image],
              item_append_pdf: {...apply_item_append_pdf},
            } as any)
          }
          // 承認待ちの場合は、編集不可
          if (response.data.apply_status === ApplyStatusType._0_WAIT) {
            setFormIsDirty(false);
          }
        } catch (err) {
          Utils.errorAction(ctx, err);
        }
      } else {
        // 新規
        const data = Utils.Model.createApplyItemDetailData();
        // Utils.Dummy.setDummyApplyItemData(data);
        setData(data);
      }

      ctx.loading.setState(false);
    };
    f();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) {
    return <div></div>;
  }

  if (!confirm) {
    return (
      <div className="px-12 py-4">
        <Helmet>
          <title>出品登録</title>
        </Helmet>
        <div className="border-b m-4">
          <PageTitle title="出品登録" />
        </div>
        {data.apply_status === ApplyStatusType._2_REPUDIATION && (
          <div className="mb-4 border p-4">
            <div className="text-red-600 mb-4">
              ※&nbsp;この出品登録申請は管理会社によって否認されました。否認理由を確認してください。
            </div>
            {/* <div className="">
              否認理由&nbsp;:&nbsp;{data.apply_deny_reason}
            </div> */}
            <div className="">コメント&nbsp;:&nbsp;{data.apply_comment}</div>
          </div>
        )}
        <div className="p-4">
          <ExhibitForms
            ctx={ctx}
            category={category.current}
            limitedLists={limitedLists.current}
            hideLists={hideLists.current}
            otherItems={otherItems.current}
            data={data}
            disabled={
              !!data.apply_item_cd &&
              data.apply_status === ApplyStatusType._0_WAIT
            }
            isImageNew={!!item_cd}
          />
        </div>
        {data.apply_status === ApplyStatusType._2_REPUDIATION && data.sale_status !== null && (
          <div className="flex justify-center m-8">
            <button className="gray px-4 py-2" onClick={onCancel}>
              申請取消
            </button>
          </div>
        )}
        {(data.apply_status === ApplyStatusType._1_APPROVED ||
          data.apply_status === ApplyStatusType._3_IMMEDIATE) && (
          <>
            <div className="flex justify-center m-8">
              <button
                className={`${ data.sale_status === SaleStatusType._2_TEISHI ? 'secondary-disabled w-9/12 py-2' : 'secondary w-9/12 py-2'}`}
                onClick={() => {
                  onSaleStatusChange(false);
                }}
                disabled={data.sale_status === SaleStatusType._2_TEISHI}
              >
                販売を停止する
              </button>
            </div>
            <div className="flex justify-center m-8">
              <button
                className={`${ data.sale_status === SaleStatusType._1_HANBAI_CHU ? 'secondary-disabled w-9/12 py-2' : 'secondary w-9/12 py-2'}`}
                onClick={() => {
                  onSaleStatusChange(true);
                }}
                disabled={data.sale_status === SaleStatusType._1_HANBAI_CHU}
              >
                販売を再開する
              </button>
            </div>
          </>
        )}
        {(!item_cd || data.apply_status !== ApplyStatusType._0_WAIT) && (
          <div className="flex justify-center m-8">
            <button className="primary px-4 py-2" onClick={onToConfirm}>
              入力内容の確認
            </button>
          </div>
        )}
        <ConfirmDialog
          modalIsOpen={!!confirmDialog}
          message={confirmDialog?.message ?? ""}
          action={confirmDialog?.callback ?? (() => {})}
        />
      </div>
    );
  }

  return (
    <div className="px-12 py-4">
      <Helmet>
        <title>出品登録</title>
      </Helmet>
      <div className="border-b my-4">
        <PageTitle title="出品登録" />
      </div>
      <div className="mt-8">{getConfirmTitle()}</div>
      <div className="p-4">
        <ExhibitFormsConfirm
          category={category.current}
          limitedLists={limitedLists.current}
          hideLists={hideLists.current}
          data={data}
          deepFileChanged={deepFileChanged}
        />
      </div>
      <div className="flex justify-center gap-4 m-8">
        <button className="primary px-4 py-2" onClick={onNext}>
          {getConfirmButtonLabel()}
        </button>
        <button className="secondary px-4 py-2" onClick={onBack}>
          戻る
        </button>
      </div>
    </div>
  );
};
export default ExhibitEntryPage;
